/**
 * `ModuleSerienbrief` — the Stammdaten → Kommunikation → Serienbriefe module
 * screen (1:1 with the prototype `showSerienbrief` block, source lines 965–1045
 * + the floating "Versandkanäle" overlay at line 1844). A letter-builder: step
 * dots + Bearbeiten/Vorschau toggle, a formatting toolbar, and the
 * "Mietbescheinigung 2024" letter preview sheet with green placeholder chips.
 *
 * Sidebar = the shared `StammdatenSidebar` (active = "serienbrief"). The whole
 * interior is `position:relative` so the Versandkanäle callout can float at the
 * bottom-right, exactly as in the prototype.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Braces,
  ChevronDown,
  ChevronRight,
  Italic,
  List,
  ListOrdered,
  Paperclip,
  Pencil,
  Underline,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { StatusDot } from "../../hero-primitives-app";
import { ChannelLogoRow } from "./channel-logo-row";
import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Inline-block green placeholder chip ({Anrede}, {Vorname}, …). */
function Chip(props: { readonly children: ReactNode }): ReactNode {
  return (
    <span
      style={{
        display: "inline-block",
        background: ld("#E7F3EC", "#28322d"),
        color: ld("#2E8557", "#6cc08e"),
        fontWeight: 500,
        padding: "0 6px",
        borderRadius: 5,
      }}
    >
      {props.children}
    </span>
  );
}

/** One step dot in the 4-step wizard rail (active = filled, else outlined). */
function StepDot(props: {
  readonly n: number;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: active ? H.accent : "transparent",
        border: active ? "none" : `1.5px solid ${ld("#DDE0E5", "#3a3d42")}`,
        color: active ? ld("#fff", "#181b1f") : ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {props.n}
    </span>
  );
}

const STEP_CONNECTOR: CSSProperties = {
  width: 30,
  height: 1.5,
  background: ld("#E2E5EA", "#36393d"),
  display: "inline-block",
};

/** A square icon button in the formatting toolbar. */
function ToolbarIcon(props: {
  readonly children: ReactNode;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: 7,
        background: active ? ld("#F1F2F4", "#2b2e32") : "transparent",
        color: active ? H.ink : ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {props.children}
    </span>
  );
}

const TOOLBAR_DROPDOWN: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 30,
  padding: "0 10px",
  border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
  borderRadius: 7,
  background: ld("#fff", "#181b1f"),
  fontSize: 12.5,
  cursor: "pointer",
};

const TOOLBAR_SEP: CSSProperties = {
  width: 1,
  height: 20,
  background: ld("#E7E9ED", "#32363a"),
  margin: "0 3px",
  display: "inline-block",
};

export function SerienbriefChannelsOverlay(): ReactNode {
  const t = useTranslations("marketing.moduleSerienbrief");
  return (
    <ChannelLogoRow
      items={[
        { channel: "letter", label: t("ePost") },
        { channel: "imap", label: t("eMail") },
        { channel: "portal", label: t("portal") },
      ]}
      style={{ position: "absolute", right: 28, bottom: -36, zIndex: 35 }}
    />
  );
}

export function ModuleSerienbrief(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleSerienbrief");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="serienbrief" setActive={props.setActive} />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          overflow: "hidden",
        }}
      >
        {/* Header: title + step rail + Bearbeiten/Vorschau toggle + Weiter */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                style={{ fontSize: 18, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {t("mietbescheinigung2024")}
              </span>
              <span
                style={{
                  color: ld("rgba(11,15,25,0.34)", "rgba(250,250,250,0.34)"),
                  display: "inline-flex",
                  cursor: "pointer",
                }}
              >
                <Pencil width={14} height={14} strokeWidth={1.8} />
              </span>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <StepDot n={1} active />
              <span style={STEP_CONNECTOR} />
              <StepDot n={2} />
              <span style={STEP_CONNECTOR} />
              <StepDot n={3} />
              <span style={STEP_CONNECTOR} />
              <StepDot n={4} />
            </div>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: 3,
              background: ld("#F1F2F4", "#2b2e32"),
              borderRadius: 9,
              gap: 2,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                padding: "5px 13px",
                borderRadius: 7,
                background: ld("#fff", "#181b1f"),
                fontSize: 12.5,
                fontWeight: 600,
                color: H.ink,
                boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}`,
              }}
            >
              {t("bearbeiten")}
            </span>
            <span
              style={{
                padding: "5px 13px",
                borderRadius: 7,
                fontSize: 12.5,
                color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
                cursor: "pointer",
              }}
            >
              {t("vorschau")}
            </span>
          </div>

          <button
            type="button"
            style={{
              appearance: "none",
              cursor: "pointer",
              fontFamily: H.sans,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              height: 36,
              padding: "0 15px",
              border: "none",
              borderRadius: 9,
              background: H.accent,
              color: ld("#fff", "#181b1f"),
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {t("weiter")}{" "}
            <ChevronRight width={15} height={15} strokeWidth={2.2} />
          </button>
        </div>

        {/* Formatting toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            flexWrap: "wrap",
            padding: "8px 10px",
            border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            borderBottom: "none",
            borderRadius: "12px 12px 0 0",
            background: ld("#FCFCFB", "#1c1f22"),
          }}
        >
          <span style={TOOLBAR_DROPDOWN}>
            {t("text")} <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span style={TOOLBAR_DROPDOWN}>
            13 <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon>
            <Bold width={15} height={15} strokeWidth={2.2} />
          </ToolbarIcon>
          <ToolbarIcon>
            <Italic width={15} height={15} strokeWidth={2} />
          </ToolbarIcon>
          <ToolbarIcon>
            <Underline width={15} height={15} strokeWidth={2} />
          </ToolbarIcon>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
              cursor: "pointer",
              gap: 1,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
              {t("a")}
            </span>
            <span
              style={{
                width: 15,
                height: 2.5,
                borderRadius: 2,
                background: "#D8453E",
              }}
            />
          </span>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon>
            <List width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <ListOrdered width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon active>
            <AlignLeft width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <AlignCenter width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <AlignRight width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <span style={{ marginLeft: "auto" }} />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 30,
              padding: "0 11px",
              border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
              borderRadius: 7,
              background: ld("#fff", "#181b1f"),
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <span style={{ color: H.accent, display: "inline-flex" }}>
              <Braces width={14} height={14} strokeWidth={1.9} />
            </span>
            {t("variablen")}{" "}
            <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 30,
              padding: "0 11px",
              border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
              borderRadius: 7,
              background: ld("#fff", "#181b1f"),
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <Paperclip width={14} height={14} strokeWidth={1.9} />
            {t("anhaenge")}
          </span>
        </div>

        {/* Letter preview sheet */}
        <div
          style={{
            border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            borderRadius: "0 0 12px 12px",
            background: ld("#F4F5F7", "#272a2e"),
            padding: "26px 0 38px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 660,
              maxWidth: "100%",
              background: ld("#fff", "#181b1f"),
              border: `1px solid ${ld("#E6E8EC", "#33363b")}`,
              borderRadius: 3,
              boxShadow: `0 12px 32px -14px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
              padding: "52px 60px 60px",
              fontSize: 13.5,
              lineHeight: 1.72,
              color: ld("#1C2230", "#e7ebf0"),
            }}
          >
            {/* Letterhead */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                paddingBottom: 13,
                borderBottom: `2px solid ${H.accent}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: H.ink,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width={15}
                    height={12}
                    style={{ display: "block" }}
                    aria-hidden="true"
                  >
                    <path
                      d="M7.5 1.6 L13.4 10.4 L1.6 10.4 Z"
                      style={{
                        fill: ld("#fff", "#181b1f"),
                        stroke: ld("#fff", "#181b1f"),
                        strokeWidth: 2.6,
                        strokeLinejoin: "round",
                      }}
                    />
                  </svg>
                </span>
                <div>
                  <div
                    style={{
                      fontSize: 15.5,
                      fontWeight: 700,
                      letterSpacing: "0.01em",
                      color: H.ink,
                      lineHeight: 1.1,
                    }}
                  >
                    {t("powerprozesse")}
                  </div>
                  <div
                    style={{
                      fontSize: 9.5,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: H.accent,
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    {t("hausverwaltungGmbh")}
                  </div>
                </div>
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontSize: 10,
                  lineHeight: 1.7,
                  color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                }}
              >
                {t.rich("absenderAnschrift", { br: () => <br /> })}
              </div>
            </div>

            {/* Sender line + recipient block */}
            <div style={{ marginTop: 34 }}>
              <div
                style={{
                  fontSize: 9,
                  color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                  borderBottom: `0.5px solid ${ld("rgba(11,15,25,0.32)", "rgba(250,250,250,0.32)")}`,
                  paddingBottom: 2,
                  display: "inline-block",
                  letterSpacing: "0.01em",
                }}
              >
                {t("powerprozesseGmbhAdenauerring1")}
              </div>
              <div style={{ marginTop: 15, lineHeight: 1.62 }}>
                <div>
                  <Chip>{t("anrede")}</Chip> <Chip>{t("vorname")}</Chip>{" "}
                  <Chip>{t("nachname")}</Chip>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Chip>{t("strasseNr")}</Chip>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Chip>{t("plz")}</Chip> <Chip>{t("ort")}</Chip>
                </div>
              </div>
            </div>

            {/* Date */}
            <div
              style={{
                textAlign: "right",
                marginTop: 24,
                color: ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
              }}
            >
              {t("worms")} <Chip>{t("datum")}</Chip>
            </div>

            {/* Subject */}
            <div style={{ marginTop: 26, fontWeight: 700, color: H.ink }}>
              {t("mietbescheinigungFuerMietverhaeltnis")}{" "}
              <Chip>{t("mietobjekt")}</Chip>
            </div>

            {/* Body */}
            <div style={{ marginTop: 20 }}>
              {t("sehrGeehrteR")} <Chip>{t("nachname")}</Chip>,
            </div>
            <div style={{ marginTop: 14 }}>
              {t("hiermitBestaetigen")} <Chip>{t("anrede")}</Chip>{" "}
              <Chip>{t("vorname")}</Chip> <Chip>{t("nachname")}</Chip>{" "}
              {t("seit")} <Chip>{t("mietbeginn")}</Chip> {t("mieterWohnung")}{" "}
              <Chip>{t("mietobjekt")}</Chip> {t("in")}{" "}
              <Chip>{t("strasseNr")}</Chip>, <Chip>{t("plz")}</Chip>{" "}
              <Chip>{t("ort")}</Chip> {t("ist")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("monatlicheGesamtmieteBetraegtDerzeit")}{" "}
              <Chip>{t("gesamtmiete")}</Chip> {t("nettokaltmiete")}
              <Chip>{t("kaltmiete")}</Chip> {t("zzgl")}{" "}
              <Chip>{t("nebenkosten")}</Chip>{" "}
              {t("vorauszahlungFuerBetriebsHeizkosten")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("dieseBescheinigungWunschMietpartei")}{" "}
              <Chip>{t("behoerde")}</Chip> {t("ausgestellt")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("fuerRueckfragenStehenIhnen")}
            </div>
            <div style={{ marginTop: 22 }}>{t("freundlichenGruessen")}</div>
            <div style={{ marginTop: 30 }}>
              <Chip>{t("sachbearbeiter")}</Chip>
              <br />
              <span style={{ color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)") }}>
                {t("powerprozesseHausverwaltungGmbh")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
